// ======================================================================
// AUTH LOCKED — NAO MODIFICAR SEM APROVACAO EXPLICITA DO USUARIO
// Alteracoes aqui podem quebrar login em producao.
// ======================================================================
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      toast.success('Login realizado com sucesso!');
      const slug = result?.profile?.company_slug;
      const role = (result?.profile?.role || '').toUpperCase();
      const isSuperAdmin = result?.profile?.is_super_admin;
      // Admin first login: redirect to company settings if theme not configured
      if (role === 'ADMIN' && !isSuperAdmin && slug) {
        try {
          const themeRes = await axios.get(`${window.location.origin}/api/company/theme`, {
            headers: { 'Authorization': `Bearer ${result.token}`, 'X-Tenant-Slug': slug }
          });
          const t = themeRes.data;
          if (!t.logo_url && t.primary_color === '#4a3728' && !t.contact_email) {
            navigate(`/t/${slug}/company-settings`);
            return;
          }
        } catch { /* proceed to dashboard */ }
      }
      navigate(slug ? `/t/${slug}/dashboard` : '/dashboard');
    } catch (error) {
      // Precise diagnostic for every possible failure mode
      const step = error._authStep;
      const stepDetail = error._authDetail;
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const isNetworkError = !error.response && !step;
      const isCorsError = isNetworkError && (error.message || '').includes('Network Error');

      console.error('[AUTH_DIAG] Login failed:', {
        step: step || 'UNKNOWN',
        detail: stepDetail || error.message,
        httpStatus: status,
        httpDetail: detail,
        apiUrl: window.location.origin + '/api',
        isCors: isCorsError,
        isNetwork: isNetworkError,
        fullError: error
      });

      if (step === 'AUTH_INVALID_RESPONSE') {
        toast.error('Resposta inesperada do servidor. O backend pode estar desatualizado.');
      } else if (step === 'AUTH_SESSION_CREATE_FAILED') {
        toast.error('Erro ao salvar sessao. Verifique se o navegador permite armazenamento local.');
      } else if (isCorsError) {
        toast.error('Erro de conexao (CORS). O deploy pode estar desatualizado. Tente Ctrl+Shift+R.');
      } else if (isNetworkError) {
        toast.error('Servidor indisponivel. Verifique sua conexao.');
      } else if (status === 401) {
        toast.error(detail || 'Email ou senha incorretos');
      } else if (status === 403) {
        toast.error(detail || 'Conta desativada');
      } else if (status === 404) {
        toast.error('Endpoint de login nao encontrado. O backend pode estar desatualizado.');
      } else if (status === 500) {
        toast.error('Erro interno do servidor. Tente novamente em instantes.');
      } else if (status === 422) {
        toast.error(detail || 'Dados de login invalidos.');
      } else {
        toast.error(detail || `Erro ao fazer login (${step || status || 'desconhecido'}). Console: F12.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-b from-uiara-primary to-uiara-primary2 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 bg-uiara-sand rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-uiara-accent rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center overflow-hidden">
              <img src="/uiara-favicon-180x180.png" alt="Uiara" className="w-12 h-12 object-contain" />
            </div>
            <div>
              <h1 className="text-4xl font-heading font-bold">Uiara Guardian</h1>
            </div>
          </div>
          <p className="text-xl leading-relaxed text-white/70">
            Transforme problemas em oportunidades. Gerencie reports, recompense colaboradores e mantenha seu hotel impecavel.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden text-center">
            <div className="inline-flex items-center gap-3 mb-4">
              <img src="/uiara-favicon-180x180.png" alt="Uiara" className="w-10 h-10" />
              <h1 className="text-2xl font-heading font-bold text-foreground">Uiara Guardian</h1>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-border p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-heading font-bold text-foreground mb-2">Bem-vindo de volta</h2>
              <p className="text-muted-foreground">Entre com suas credenciais para acessar o sistema</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  data-testid="login-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11 focus:ring-uiara-sand focus:border-uiara-sand"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <PasswordInput
                  id="password"
                  placeholder="--------"
                  data-testid="login-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-11 focus:ring-uiara-sand focus:border-uiara-sand"
                />
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full h-11 font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>

              <div className="text-center pt-1">
                <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary hover:underline" data-testid="forgot-password-link">
                  Esqueci minha senha
                </Link>
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              Acesso somente por convite
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
